import { gql } from '@apollo/client'

export const TICKET_ORDER_COMPLETE_FRAGMENT = gql`
  fragment TicketOrderCompleteSummary on ReservationObject {
    id
    showtimeId
    email
    venueId
    referenceNumber
    canCancel
    seatIds
    status {
      description
      reference
    }
    ticketOrder {
      id
      qrCodeSvgXml
      qrData
      lines {
        tickets {
          amount {
            currencyAmount
            currencyCode
          }
          discounts {
            code
            name
            value {
              currencyAmount
              currencyCode
            }
          }
          seatName
          seatId
          discounts {
            category
          }
        }
      }
      totals {
        value {
          currencyAmount
          currencyCode
        }
        grandTotal {
          currencyAmount
          currencyCode
        }
        discount {
          currencyAmount
          currencyCode
        }
        discountPercent
        tax {
          currencyAmount
          currencyCode
        }
        subtotal {
          currencyAmount
          currencyCode
        }
      }
    }
    showtimeSummary {
      releaseType
      utcStartTime
      localStartTimeFriendlyString
      localStartDateFriendlyString
    }
    venueSummary {
      id
      name
      address {
        city
        line
        zipCode
        state
        countryCode
      }
    }
  }
`

export const TICKET_PREVIEW_FRAGMENT = gql`
  fragment TicketPreviewFragment on ReservationObject {
    id
    showtimeId
    venueId
    guildSubscription
    seatIds
    email
    leaseExpiration
    isChangeReservation
    friendReservationId
    intendedSeatQuantityWithProducts {
      offerId
      productId
      quantity
    }
    pendingDiscounts {
      category
      description
      id
      remainingUses
    }
    priorReservation {
      id
      email
      theatricalSlug
      showtimeId
    }
    status {
      reference
      description
    }
    showtimeSummary {
      releaseType
      utcStartTime
      localStartTimeFriendlyString
      localStartDateFriendlyString
      seatSelectionLimit
    }
    theatricalSlug
    venueSummary {
      id
      name
      address {
        city
        line
        zipCode
        state
        countryCode
      }
    }
    ticketOrder {
      id
      currencyCode
      paymentIntent {
        clientSecret
        paymentIntentId
      }
      paypalOrder {
        paypalOrderId
        paypalApprovalUrl
      }
      discounts {
        id
        code
        name
        description
        category
        value {
          currencyCode
          currencyAmount
        }
      }
      lines {
        sweepstakesEntries {
          entriesQuantity
          entryType
        }
        fees {
          description
          discounts {
            id
            description
            name
            code
            category
            value {
              currencyAmount
              currencyCode
            }
          }
          amount {
            currencyAmount
            currencyCode
          }
        }
        taxes {
          description
          taxRuleId
          amount {
            currencyAmount
            currencyCode
          }
        }
        tickets {
          description
          seatId
          seatName
          amount {
            currencyAmount
            currencyCode
          }
          discounts {
            id
            code
            description
            name
            category
            value {
              currencyAmount
              currencyCode
            }
          }
          rate {
            description
            offerId
            productId
            rateLabel
            price {
              currencyAmount
              currencyCode
            }
          }
        }
        guildSubscription {
          description
          discounts {
            description
            id
            code
            name
            value {
              currencyCode
              currencyAmount
            }
          }
          price {
            currencyAmount
            currencyCode
          }
        }
        payItForwards {
          description
          quantity
          amount {
            currencyAmount
            currencyCode
          }
          price {
            currencyCode
            currencyAmount
          }
        }
      }
      totals {
        grandTotal {
          currencyAmount
          currencyCode
        }
        discount {
          currencyAmount
          currencyCode
        }
        discountPercent
        tax {
          currencyAmount
          currencyCode
        }
        subtotal {
          currencyAmount
          currencyCode
        }
      }
    }
  }
`

export const SET_SEAT_QUANTITIES = gql`
  mutation setSeatQuantities($input: UpdateReservationSeatQuantityInput!) {
    updateReservationSeatQuantity(input: $input) {
      errors {
        reference
        description
      }
      reservation {
        id
        seatQuantity
        ticketOrder {
          lines {
            tickets {
              amount {
                currencyAmount
                currencyCode
              }
            }
          }
        }
      }
      errors {
        description
        reference
      }
    }
  }
`

const RESERVATION_WITH_SHOWTIME_DATA_FRAGMENT = gql`
  fragment ReservationWithShowtimeDataFragment on ReservationObject {
    id
    showtimeId
    venueId
    seatIds
    leaseExpiration
    isChangeReservation
    friendReservationId
    intendedSeatQuantityWithProducts {
      offerId
      productId
      quantity
    }
    serviceFee {
      price {
        currencyAmount
        currencyCode
      }
      mandatory
    }
    pendingDiscounts {
      batchId
      name
      description
      category
      remainingUses
    }
    priorReservation {
      id
      showtimeId
      theatricalSlug
      seatIds
      ticketOrder {
        totals {
          grandTotal {
            currencyAmount
            currencyCode
          }
          discount {
            currencyAmount
            currencyCode
          }
        }
      }
    }
    status {
      reference
      description
    }
    venueSummary {
      id
      name
      address {
        city
        line
        zipCode
        state
        countryCode
      }
    }
    showtimeSummary {
      utcStartTime
      localStartTimeFriendlyString
      localStartDateFriendlyString
      releaseType
      seatSelectionLimit
    }
    adjacentShowtimes {
      showtimes {
        venueId
        showtimeId
        localFriendlyStartDay
        localFriendlyStartTime
        isReservable
      }
    }
    showtimeSeatingInfo {
      rowCount
      seatsReservable
      seatTypesPresent(useV4: $useV4) {
        reference
        description
        icons {
          default {
            cloudinaryPath
          }
        }
      }
      seatRates {
        description
        rateLabel
        productId
        offerId
        areaCategory
        areaDescription
        reference
        basePrice {
          currencyCode
          currencyAmount
        }
        discountedPrice {
          currencyCode
          currencyAmount
        }
        price {
          currencyCode
          currencyAmount
        }
      }
      columnCount
      seats {
        seatId
        isAvailable
        areaCategory
        rates {
          description
          reference
          discountedPrice {
            currencyCode
            currencyAmount
          }
          price {
            currencyCode
            currencyAmount
          }
        }
        seatName
        seatType(useV4: $useV4) {
          reference
          description
          icons {
            default {
              cloudinaryPath
            }
            selected {
              cloudinaryPath
            }
          }
        }
        xCoord
        yCoord
      }
    }
  }
`

export const GET_RESERVATION_WITH_SHOWTIME_DATA = gql`
  ${RESERVATION_WITH_SHOWTIME_DATA_FRAGMENT}
  query getReservationWithShowtimeData($input: GetReservationInput!, $useV4: Boolean!) {
    getReservation(input: $input) {
      reservation {
        ...ReservationWithShowtimeDataFragment
      }
      errors {
        reference
        description
      }
    }
  }
`

export const SET_RESERVATION_SHOWTIME_ID = gql`
  ${RESERVATION_WITH_SHOWTIME_DATA_FRAGMENT}
  mutation setReservationShowtimeId($input: UpdateReservationShowtimeIdInput!, $useV4: Boolean!) {
    updateReservationShowtimeId(input: $input) {
      reservation {
        ...ReservationWithShowtimeDataFragment
      }
      errors {
        reference
        description
      }
    }
  }
`

export const RESERVE_SEATS = gql`
  ${TICKET_PREVIEW_FRAGMENT}
  mutation reserveSeats($input: UpdateReservationSeatIdsInput!) {
    updateReservationSeatIds(input: $input) {
      reservation {
        id
        ...TicketPreviewFragment
      }
      errors {
        reference
        description
      }
    }
  }
`

export const GET_TICKET_ORDER_PREVIEW = gql`
  ${TICKET_PREVIEW_FRAGMENT}
  query GetReservation($input: GetReservationInput!) {
    getReservation(input: $input) {
      reservation {
        id
        isGuildSubscriber
        ...TicketPreviewFragment
      }
      errors {
        reference
        description
      }
    }
  }
`

export const COMMIT_TICKET_RESERVATION = gql`
  ${TICKET_ORDER_COMPLETE_FRAGMENT}
  mutation commitTicketReservation($input: CommitReservationInput!) {
    commitReservation(input: $input) {
      reservation {
        id
        ...TicketOrderCompleteSummary
      }
      errors {
        reference
        description
      }
    }
  }
`

export const UPDATE_RESERVATION_EMAIL = gql`
  mutation updateReservationEmail($input: UpdateReservationEmailInput!) {
    updateReservationEmail(input: $input) {
      reservation {
        id
        email
        ticketOrder {
          paymentIntent {
            clientSecret
          }
        }
      }
      errors {
        reference
        description
      }
    }
  }
`

export const RESEND_EMAIL = gql`
  mutation resendEmail($input: ResendEmailInput!) {
    resendEmail(input: $input) {
      reservation {
        id
        email
      }
      errors {
        reference
        description
      }
    }
  }
`

export const GET_BONUS_STREAMS = gql`
  query getBonusStreams($include: [String]!) {
    listWatchables(first: 100, input: { filter: { type: { in: [MOVIE] }, projectSlug: { in: $include } } }) {
      edges {
        node {
          __typename
          ... on ContentMovie {
            id
            portraitImage: image(aspect: "2:3", category: NON_TITLE_ART) {
              cloudinaryPath
            }
            landscapeImage: image(aspect: "16:9", category: ANGEL_KEY_ART_1) {
              cloudinaryPath
            }
            project {
              id
              slug
              name
            }
          }
        }
      }
    }
  }
`
